import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Box,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  AppDatePicker,
  AppInput,
  AppSelect,
  AppTextarea,
  OutlineButton,
  PrimaryButton,
} from '../../../../components/ui';
import {
  WORK_AREA_OPTIONS,
  WORKING_HOURS_OPTIONS,
  UOM_SELECT_OPTIONS,
} from '../../../../data/master/indents';
import { sitesForBu } from '../../../../data/master/businessUnits';
import {
  lookupBusinessUnits,
  lookupSites,
  lookupEquipmentTypes,
} from '../../../../repositories/lookup.repository';

import { createIndent } from '../../../../repositories/indent.repository';
import { listVendors } from '../../../../repositories/vendor.repository';
import { dbIdForCode } from '../../../../utils/entityResolve';
import { formatTimePeriod } from '../../../../utils/timePeriod';

const empty = {
  vendorId: '',
  buId: '',
  siteId: '',
  workArea: 'Project',
  raisedByName: '',
  raisedByEmail: '',
  equipmentType: '',
  specification: '',
  quantity: '1',
  uom: 'Nos.',
  capacity: '',
  capacityUom: '',
  requiredFrom: '',
  requiredTill: '',
  workingHours: '12 Hrs',
  remarks: '',
};

export function IndentFormModal({
  isOpen,
  onClose,
  onSaved,
  presetVendorId = null,
  presetVendor = null,
}) {
  const toast = useToast();
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [vendors, setVendors] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [sites, setSites] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const [vendorRes, buRes, siteRes, typeRes] = await Promise.all([
          listVendors({ pageSize: 200 }),
          lookupBusinessUnits(),
          lookupSites(),
          lookupEquipmentTypes(),
        ]);
        if (cancelled) return;
        const vendorList = vendorRes.items || [];
        setVendors(vendorList);
        setBusinessUnits(buRes || []);
        setSites(siteRes || []);
        setEquipmentTypes(typeRes || []);
        const vendor =
          presetVendor ||
          vendorList.find((v) => v.vendorId === presetVendorId || v.id === presetVendorId) ||
          null;
        setForm({
          ...empty,
          vendorId: vendor?.vendorId || '',
          buId: vendor?.buId || '',
          siteId: vendor?.siteId || '',
          raisedByName: vendor?.ownerFullName || '',
          raisedByEmail: vendor?.email || '',
        });
        setErrors({});
      } catch (err) {
        if (!cancelled) {
          toast({
            title: err?.message || 'Failed to load form options',
            status: 'error',
            position: 'top-right',
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, presetVendorId, presetVendor, toast]);

  const findVendorById = (id) => vendors.find((v) => v.vendorId === id) || null;

  const vendorOptions = useMemo(
    () =>
      vendors
        .filter((v) => v.verificationStatus === 'Verified' && v.active !== false)
        .map((v) => ({
          value: v.vendorId,
          label: v.tradeAgencyName,
          description: v.vendorId,
        })),
    [vendors],
  );

  const equipmentOptions = useMemo(
    () =>
      equipmentTypes
        .filter((e) => e.active !== false)
        .map((e) => ({ value: e.name, label: e.name, description: e.category })),
    [equipmentTypes],
  );

  const buOptions = useMemo(
    () =>
      businessUnits
        .filter((b) => b.active !== false)
        .map((b) => ({
          value: b.buId,
          label: b.shortName,
          description: b.fullNameGst || b.buId,
        })),
    [businessUnits],
  );

  const sitesForSelectedBu = useMemo(
    () => sitesForBu(sites, form.buId),
    [sites, form.buId],
  );

  const siteOptions = useMemo(
    () =>
      sitesForSelectedBu.map((s) => ({
        value: s.siteId,
        label: s.siteName,
        description: s.projectOperation || s.siteId,
      })),
    [sitesForSelectedBu],
  );

  const showSiteField = Boolean(form.buId && sitesForSelectedBu.length > 0);

  const timePeriod = useMemo(
    () => formatTimePeriod(form.requiredFrom, form.requiredTill),
    [form.requiredFrom, form.requiredTill],
  );

  const patch = (partial) => {
    setForm((f) => ({ ...f, ...partial }));
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(partial).forEach((k) => delete next[k]);
      return next;
    });
  };

  const onVendorChange = (vendorId) => {
    const v = findVendorById(vendorId);
    patch({
      vendorId: vendorId || '',
      buId: v?.buId || '',
      siteId: v?.siteId || '',
      raisedByName: v?.ownerFullName || form.raisedByName,
      raisedByEmail: v?.email || form.raisedByEmail,
    });
  };

  const validate = () => {
    const e = {};
    if (!form.vendorId) e.vendorId = 'Select vendor';
    if (!form.buId) e.buId = 'Select Business Unit';
    if (!form.siteId) e.siteId = 'Select Site';
    if (!form.equipmentType) e.equipmentType = 'Select equipment type';
    if (!form.requiredFrom) e.requiredFrom = 'Required from date is required';
    if (!form.requiredTill) e.requiredTill = 'Required till date is required';
    if (
      form.requiredFrom &&
      form.requiredTill &&
      form.requiredTill < form.requiredFrom
    ) {
      e.requiredTill = 'Must be on or after From date';
    }
    return e;
  };

  const handleSave = async (ev) => {
    ev.preventDefault();
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      toast({
        title: 'Fix the highlighted fields',
        status: 'warning',
        position: 'top-right',
      });
      return;
    }

    const vendor = findVendorById(form.vendorId);
    const typeRow = equipmentTypes.find((t) => t.name === form.equipmentType);
    if (!typeRow?.id) {
      toast({ title: 'Select a valid equipment type', status: 'error', position: 'top-right' });
      return;
    }

    setSaving(true);
    let indent;
    try {
    indent = await createIndent({
      vendorId: form.vendorId,
      vendorDbId: vendor?.id || dbIdForCode(vendors, form.vendorId, { codeKey: 'vendorId' }),
      buId: form.buId,
      buDbId: dbIdForCode(businessUnits, form.buId, { codeKey: 'buId' }),
      siteId: form.siteId,
      siteDbId: dbIdForCode(sites, form.siteId, { codeKey: 'siteId' }),
      workArea: form.workArea,
      raisedByName: form.raisedByName.trim(),
      raisedByEmail: form.raisedByEmail.trim(),
      line: {
        equipmentTypeId: typeRow.id,
        equipmentType: form.equipmentType,
        specification: form.specification.trim() || form.equipmentType,
        quantity: Number(form.quantity) || 1,
        uom: form.uom,
        capacity: form.capacity.trim(),
        capacityUom: form.capacityUom.trim(),
        requiredFrom: form.requiredFrom,
        requiredTill: form.requiredTill,
        timePeriod: formatTimePeriod(form.requiredFrom, form.requiredTill),
        workingHours: form.workingHours,
        remarks: form.remarks.trim(),
      },
      // legacy flat fields for mock mode compatibility
      equipmentType: form.equipmentType,
      specification: form.specification.trim() || form.equipmentType,
      quantity: form.quantity,
      uom: form.uom,
      capacity: form.capacity.trim(),
      capacityUom: form.capacityUom.trim(),
      requiredFrom: form.requiredFrom,
      requiredTill: form.requiredTill,
      timePeriod: formatTimePeriod(form.requiredFrom, form.requiredTill),
      workingHours: form.workingHours,
      remarks: form.remarks.trim(),
    });

    const bu = businessUnits.find((b) => b.buId === form.buId);
    const site = sites.find((s) => s.siteId === form.siteId);
    toast({
      title: 'Request created',
      description: `${indent.indentId} · ${form.equipmentType} · ${bu?.shortName || form.buId} / ${site?.siteName || form.siteId}`,
      status: 'success',
      position: 'top-right',
    });
    onSaved?.(indent);
    onClose();
    } catch (err) {
      toast({
        title: err?.message || 'Failed to create indent',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="4xl"
      isCentered
      scrollBehavior="inside"
      trapFocus={false}
    >
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        as="form"
        onSubmit={handleSave}
        bg="bg.surface"
        borderRadius="md"
        mx={4}
        my={4}
        maxH="90vh"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <ModalHeader flexShrink={0}>Indenter Form / Request</ModalHeader>
        <ModalCloseButton />
        <ModalBody flex="1" overflowY="auto" minH={0} pb={4}>
          <VStack align="stretch" spacing={5}>
            <Text fontSize="13px" color="text.secondary">
              BU & Site default from the selected Vendor (editable). Utkarsh can auto-recommend
              free nearby equipment after save.
            </Text>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <AppSelect
                label="Vendor (Agency)"
                isRequired
                isSearchable
                searchPlaceholder="Search vendor…"
                placeholder="Select verified vendor"
                value={form.vendorId}
                onChange={onVendorChange}
                options={vendorOptions}
                isDisabled={!!presetVendorId}
                isInvalid={!!errors.vendorId}
                errorMessage={errors.vendorId}
              />
              <AppSelect
                label="Work area"
                value={form.workArea}
                onChange={(v) => patch({ workArea: v })}
                options={WORK_AREA_OPTIONS.map((w) => ({ value: w, label: w }))}
              />
              <AppSelect
                label="Business Unit"
                isRequired
                isSearchable
                value={form.buId}
                onChange={(buId) => patch({ buId: buId || '', siteId: '' })}
                options={buOptions}
                isDisabled={!!presetVendorId}
                isInvalid={!!errors.buId}
                errorMessage={errors.buId}
                helperText={
                  presetVendorId ? 'Taken from this vendor — not editable here.' : undefined
                }
              />
              {showSiteField ? (
                <AppSelect
                  label="Site"
                  isRequired
                  isSearchable
                  placeholder="Select site"
                  value={form.siteId}
                  onChange={(siteId) => patch({ siteId: siteId || '' })}
                  options={siteOptions}
                  isDisabled={!!presetVendorId}
                  isInvalid={!!errors.siteId}
                  errorMessage={errors.siteId}
                  helperText={
                    presetVendorId ? 'Taken from this vendor — not editable here.' : undefined
                  }
                />
              ) : null}
              <AppInput
                label="Raised by (name)"
                value={form.raisedByName}
                onChange={(e) => patch({ raisedByName: e.target.value })}
              />
              <AppInput
                label="Raised by (email)"
                type="email"
                value={form.raisedByEmail}
                onChange={(e) => patch({ raisedByEmail: e.target.value })}
              />
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
              <AppSelect
                label="Required equipment"
                isRequired
                isSearchable
                value={form.equipmentType}
                onChange={(v) =>
                  patch({
                    equipmentType: v || '',
                    specification: form.specification || v || '',
                  })
                }
                options={equipmentOptions}
                isInvalid={!!errors.equipmentType}
                errorMessage={errors.equipmentType}
              />
              <AppInput
                label={
                  form.equipmentType
                    ? `Specification ${form.equipmentType}`
                    : 'Specification'
                }
                value={form.specification}
                onChange={(e) => patch({ specification: e.target.value })}
                placeholder={
                  form.equipmentType
                    ? `What ${form.equipmentType} specification do you need?`
                    : 'e.g. 15 Tn Pick & Carry'
                }
              />
              <AppInput
                label="Quantity"
                value={form.quantity}
                onChange={(e) =>
                  patch({ quantity: e.target.value.replace(/\D/g, '').slice(0, 4) })
                }
              />
              <AppSelect
                label="UOM"
                value={form.uom}
                onChange={(v) => patch({ uom: v || 'Nos.' })}
                options={UOM_SELECT_OPTIONS}
              />
              <AppInput
                label="Capacity"
                value={form.capacity}
                onChange={(e) => patch({ capacity: e.target.value })}
                placeholder="e.g. 15"
              />
              <AppInput
                label="Capacity UOM"
                value={form.capacityUom}
                onChange={(e) => patch({ capacityUom: e.target.value })}
                placeholder="T / FT / CUM"
              />
              <AppDatePicker
                label="Required from"
                isRequired
                value={form.requiredFrom}
                onChange={(e) => patch({ requiredFrom: e.target.value })}
                isInvalid={!!errors.requiredFrom}
                errorMessage={errors.requiredFrom}
              />
              <AppDatePicker
                label="Required till"
                isRequired
                value={form.requiredTill}
                onChange={(e) => patch({ requiredTill: e.target.value })}
                isInvalid={!!errors.requiredTill}
                errorMessage={errors.requiredTill}
              />
              <AppInput
                label="Time period"
                value={timePeriod}
                isReadOnly
                placeholder="Auto from dates"
                helperText="Calculated from Required from → till (Years / Months / Days)."
              />
              <AppSelect
                label="Working hours"
                value={form.workingHours}
                onChange={(v) => patch({ workingHours: v })}
                options={WORKING_HOURS_OPTIONS.map((w) => ({ value: w, label: w }))}
              />
            </SimpleGrid>

            <AppTextarea
              label="Remarks"
              value={form.remarks}
              onChange={(e) => patch({ remarks: e.target.value })}
              rows={3}
              placeholder="With operators / without diesel / site notes…"
            />
          </VStack>
        </ModalBody>
        <ModalFooter
          flexShrink={0}
          gap={2}
          borderTopWidth="1px"
          borderColor="border.subtle"
        >
          <OutlineButton type="button" onClick={onClose}>
            Cancel
          </OutlineButton>
          <PrimaryButton type="submit">Submit request</PrimaryButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
